/* eslint-disable no-console */

/**
 * Simple browser debug logger for Post Office components.
 *
 * NOTE: This is a copy of
 * `post-office/packages/util/shared-contracts/src/util/in-browser-debug-logger.ts`
 * to avoid a cross-workspace import. You may want to keep them in sync.
 *
 * Usage:
 * - Enable:  window.postOffice{Component}Debug = true
 * - Disable: window.postOffice{Component}Debug = false
 *
 * Example:
 * ```typescript
 * const logger = createInBrowserDebugLogger('Choreographer');
 * logger.debug('Component loaded', { config: 'value' });
 * ```
 */

interface InBrowserDebugLogger {
	debug: (message: string, data?: Record<string, unknown>) => void;
	info: (message: string, data?: Record<string, unknown>) => void;
	warn: (message: string, data?: Record<string, unknown>) => void;
	error: (message: string, data?: Record<string, unknown>) => void;
}

/**
 * Creates a simple in-browser debug logger. Do NOT use this to log any PII/UGC.
 *
 * This only logs if the user opts in by setting window.postOffice{Component}Debug = true
 *
 * It can then be disabled via window.postOffice{Component}Debug = false
 */

export const createInBrowserDebugLogger = (component: string): InBrowserDebugLogger => {
	const windowKey = `postOffice${component}Debug`;

	const isEnabled = (): boolean => {
		try {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
			return typeof window !== 'undefined' && (window as any)[windowKey] === true;
		} catch {
			return false;
		}
	};

	const log = (level: string, message: string, data?: Record<string, unknown>) => {
		if (!isEnabled()) return;

		const prefix = `[Post Office - ${component}]`;

		// Use appropriate console method for each level
		if (level === 'debug') {
			console.log(`${prefix} ${message}`, data || '');
		} else if (level === 'info') {
			console.info(`${prefix} ${message}`, data || '');
		} else if (level === 'warn') {
			console.warn(`${prefix} ${message}`, data || '');
		} else if (level === 'error') {
			console.error(`${prefix} ${message}`, data || '');
		}
	};

	return {
		debug: (msg, data) => log('debug', msg, data),
		info: (msg, data) => log('info', msg, data),
		warn: (msg, data) => log('warn', msg, data),
		error: (msg, data) => log('error', msg, data),
	};
};

// Export type for consumers
export type { InBrowserDebugLogger };
